import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as ArrowLeft } from '../../../assets/icons/arrow-left.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import ocpiApi from '../../../utils/api/ocpi';
import CpoRoleOperations from '../operations/cpo/CpoRoleOperations';
import EmspRoleOperations from '../operations/emsp/EmspRoleOperations';

/**
 * OCPI Role Operations Page
 * @returns {any} The rendered component.
 */
export default function OCPIRoleOperations(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { roleId } = useParams<{ roleId: string }>();

  const [loading, setLoading] = useState(true);
  const [roleData, setRoleData] = useState<any>(null);

  useEffect(() => {
    dispatch(setPageTitle('OCPI Role Operations'));
  }, [dispatch]);

  useEffect(() => {
    /**
     * Fetches role data from the backend.
     */
    const fetchRoleData = async () => {
      try {
        const response = await ocpiApi.listTenants();
        const rawData = response.data || {};
        const nestedTenants = Array.isArray(rawData.tenants) ? rawData.tenants : [];
        const tenantsArray = Array.isArray(rawData) ? rawData : nestedTenants;

        const role = tenantsArray.find((t: any) => t.id === roleId || t._id === roleId);
        if (role) {
          setRoleData(role);
        }
      } catch (error) {
        console.error('Failed to fetch roles', error);
      } finally {
        setLoading(false);
      }
    };

    if (roleId) {
      fetchRoleData();
    } else {
      setLoading(false);
    }
  }, [roleId]);

  if (loading) {
    return (
      <div className="ocpi-stepper-page">
        <div className="loader-wrap">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  if (!roleData) {
    return (
      <div className="ocpi-stepper-page">
        <div className="ocpi-role-not-found">
          <h3>Role Not Found</h3>
          <p>The role you are looking for does not exist or has been deleted.</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/ev-admin/ocpi')}>
            <ArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ocpi-stepper-page">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="ocpi-page-header">
        <div className="ocpi-page-header__top-row">
          <button type="button" className="ocpi-page-header__back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft />
          </button>
        </div>
        <div className="header-text">
          <h1 className="page-title">OCPI Role Operations</h1>
          <p className="page-description">
            Manage owned modules role-wise with data table and CRUD actions.
          </p>
        </div>
      </div>

      <div className="ocpi-role-operations-actions">
        {roleData.role?.toUpperCase() === 'CPO' ? (
          <CpoRoleOperations countryCode={roleData.countryCode} partyId={roleData.partyId} />
        ) : (
          <EmspRoleOperations countryCode={roleData.countryCode} partyId={roleData.partyId} />
        )}
      </div>
    </div>
  );
}
